import CloseIcon from "@mui/icons-material/Close";
import {
  Box,
  Button,
  Checkbox,
  CircularProgress,
  IconButton,
  Modal,
  Stack,
  Typography,
} from "@mui/material";
import React, { useEffect, useState } from "react";
import { brand, gray } from "../../../../theme/themePrimitives";
import {
  StringOperator,
  NumericOperator,
  DateOperator,
  BooleanOperator,
  ListOperator,
} from "../../../../services/api/types/users/users";

interface ValuesModalProps {
  openModal: boolean;
  setOpenModal: React.Dispatch<React.SetStateAction<boolean>>;
  values: string[] | undefined;
  buttonLoading: boolean;
  valuesLoading: boolean;
  onAddSelectedValues: (selectedValues: string[]) => void;
  operator:
    | StringOperator
    | NumericOperator
    | DateOperator
    | BooleanOperator
    | ListOperator
    | "";
  preSelectedValues?: string;
}

export const ValuesModal: React.FC<ValuesModalProps> = ({
  openModal,
  setOpenModal,
  values,
  buttonLoading,
  valuesLoading,
  onAddSelectedValues,
  operator,
  preSelectedValues,
}) => {
  const [selectedValues, setSelectedValues] = useState<string[]>([]);

  useEffect(() => {
    if (openModal) {
      const existing = preSelectedValues
        ? preSelectedValues.split(/[|,]/).map((v) => v.trim()).filter(Boolean)
        : [];
      setSelectedValues(existing);
    }
  }, [operator, openModal, preSelectedValues]);

  const handleCheckboxChange = (value: string) => {
    setSelectedValues((prev) => {
      return prev.includes(value)
        ? prev.filter((v) => v !== value)
        : [...prev, value];
    });
  };

  const handleAddSelectedValues = () => {
    onAddSelectedValues(selectedValues);
    setSelectedValues([]);
  };

  return (
    <Modal open={openModal} onClose={() => setOpenModal(false)}>
      <Box
        sx={{
          position: "absolute",
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          backgroundColor: "white",
          borderRadius: "8px",
          boxShadow: "0px 3px 6px rgba(0, 0, 0, 0.24)",
          width: "50%",
          maxHeight: "90vh",
          display: "flex",
          flexDirection: "column",
        }}
      >
        {valuesLoading ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
            <CircularProgress size={100} />
          </Box>
        ) : (
          <>
            {/* Header */}
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                p: 2,
                borderBottom: `1px solid ${gray[300]}`,
                flexShrink: 0,
              }}
            >
              <Typography variant="h5">Select values</Typography>
              <IconButton onClick={() => setOpenModal(false)}>
                <CloseIcon sx={{ color: "#98A2B3" }} />
              </IconButton>
            </Box>

            {/* Scrollable content */}
            <Box
              sx={{
                flexGrow: 1,
                overflowY: "auto",
                p: 2,
                borderBottom: `1px solid ${gray[300]}`,
              }}
            >
              <Stack spacing={1}>
                {values && values.length > 0 ? (
                  values.map((v) => (
                    <Box
                      key={v}
                      sx={{
                        display: "flex",
                        gap: "1rem",
                        alignItems: "center",
                      }}
                    >
                      <Checkbox
                        checked={selectedValues.includes(v)}
                        onChange={() => handleCheckboxChange(v)}
                      />
                      <Typography>{v}</Typography>
                    </Box>
                  ))
                ) : (
                  <Typography color="text.secondary" textAlign="center">
                    No values available
                  </Typography>
                )}
              </Stack>
            </Box>

            {/* Fixed footer button */}
            <Box
              sx={{
                p: 2,
                flexShrink: 0,
                borderTop: `1px solid ${gray[300]}`,
                backgroundColor: "white",
              }}
            >
              {buttonLoading ? (
                <Box sx={{ display: "flex", justifyContent: "center" }}>
                  <CircularProgress />
                </Box>
              ) : (
                <Button
                  onClick={handleAddSelectedValues}
                  disabled={selectedValues.length === 0}
                  fullWidth
                  sx={{
                    borderRadius: "12px",
                    backgroundColor: `${brand[600]} !important`,
                    color: `white`,
                    "&:hover": {
                      backgroundColor: `${brand[700]} !important`,
                    },
                  }}
                >
                  <Typography>Add selected values to filter</Typography>
                </Button>
              )}
            </Box>
          </>
        )}
      </Box>
    </Modal>
  );
};
